import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { useIsMobile } from 'utils/hooks';
import { dataLayerPush } from 'utils/tracking';
import { translateData } from 'utils/translation';
import { Dialog, Button, Div } from 'components/Ui';
import { accountIcons } from 'images';

import { EASY_EDIT_PLAN, PREDICTIVE_ORDER, REPLENISH } from './constants';

import styles from './styles.scss';

const WhatAreMyOptionsDialog = ({
  open,
  isResidential,
  isReturnable,
  switchTo,
  onSave,
  onClose,
  choice,
}) => {
  const isCommercial = !isResidential;

  const showEasyEditPlan = switchTo === EASY_EDIT_PLAN || choice;
  const showPredictiveOrder =
    switchTo === PREDICTIVE_ORDER || (choice && isResidential && !isReturnable);
  const showReplenish =
    switchTo === REPLENISH ||
    (choice && ((isCommercial && !isReturnable) || isReturnable));
  const switchToText = choice
    ? false
    : showEasyEditPlan
    ? EASY_EDIT_PLAN
    : showPredictiveOrder
    ? PREDICTIVE_ORDER
    : showReplenish
    ? REPLENISH
    : null;
  const header1More = showEasyEditPlan ? translateData(' the') : '';

  const header1 = switchToText
    ? `${translateData('You’ve switched to')}${header1More}`
    : translateData('You have a choice between');
  const header2 = switchToText || translateData('2 delivery options:');
  const HeaderIcon = showEasyEditPlan
    ? accountIcons.EasyEditPlan
    : showPredictiveOrder
    ? accountIcons.PredictiveOrder
    : accountIcons.Replenish;

  const isMobile = useIsMobile();

  const renderEasyEditPlan = (showIconHeader) => {
    const subtext = 'Reliable and customized';
    const text1 =
      'Set the exact quantity of any product you would like to receive on each delivery. Until you decide to change it, you will always be delivered this quantity.';
    const text2 =
      'You can edit your products or quantities any time before the regular cutoff time (usually 3pm local time the day before your delivery).';

    return (
      <div className={styles.textContainer}>
        {showIconHeader && (
          <Div className={styles.iconHeaderText}>
            <div className={styles.iconContainer}>
              <img src={accountIcons.EasyEditPlan} alt="" />
            </div>
            {EASY_EDIT_PLAN}
          </Div>
        )}
        {!choice && <Div className={styles.subtext}>{subtext}</Div>}
        <br />
        <div className={classnames({ [styles.text]: showIconHeader })}>
          <Div>{text1}</Div>
          <br />
          <Div>{text2}</Div>
        </div>
      </div>
    );
  };

  const renderPredictiveOrder = (showIconHeader) => {
    const subtext = 'Set it and forget it';
    const text1 =
      'We will deliver product quantities based on your average consumption over the last few deliveries. ';
    const text2 =
      'You’re still in control; we’ll let you know what’s coming prior to each delivery, so you can make changes if you like.';

    return (
      <div className={styles.textContainer}>
        {showIconHeader && (
          <Div className={styles.iconHeaderText}>
            <div className={styles.iconContainer}>
              <img src={accountIcons.PredictiveOrder} alt="" />
            </div>
            {PREDICTIVE_ORDER}
          </Div>
        )}
        {!choice && <Div className={styles.subtext}>{subtext}</Div>}
        <br />
        <div className={classnames({ [styles.text]: showIconHeader })}>
          <Div>{text1}</Div>
          <br />
          <Div>{text2}</Div>
        </div>
      </div>
    );
  };

  const renderReplenish = (showIconHeader) => {
    const subtextResidential = 'For your 3- or 5-gallon returnables:';
    const text1Residential =
      'When you leave out your empties, we’ll replace that quantity with full bottles.';
    const text2Residential =
      'And don’t worry - if you forget to leave your empties out, we’ll leave you the average amount you’ve been getting recently.';

    const subtextCommercial = 'You control what you want in your breakroom';
    const text1Commercial =
      'Each delivery, your RSR will take inventory of all products and restock what has been consumed. You can always add more products through your RSR or customer service.';
    const text2Commercial = null;

    const subtext = isResidential ? subtextResidential : subtextCommercial;
    const text1 = isResidential ? text1Residential : text1Commercial;
    const text2 = isResidential ? text2Residential : text2Commercial;

    return (
      <div className={styles.textContainer}>
        {showIconHeader && (
          <Div className={styles.iconHeaderText}>
            <div className={styles.iconContainer}>
              <img src={accountIcons.Replenish} alt="" />
            </div>
            {REPLENISH}
          </Div>
        )}
        {!choice && <Div className={styles.subtext}>{subtext}</Div>}
        {isResidential && (
          <div className={classnames({ [styles.text]: showIconHeader })}>
            <div className={styles.twoColumnIconContainer}>
              <div className={styles.left}>
                <img src={accountIcons.LargeBottleCircle} alt="" />
              </div>
              <div className={styles.right}>
                <img src={accountIcons.LargeBottleCircleNot} alt="" />
              </div>
            </div>
            <div className={styles.twoColumnTextContainer}>
              <Div className={styles.left}>{text1}</Div>
              {text2 && <Div className={styles.right}>{text2}</Div>}
            </div>
          </div>
        )}
        {isCommercial && (
          <div className={classnames({ [styles.text]: showIconHeader })}>
            <br />
            <Div>{text1}</Div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog
      size="large"
      fullScreen={isMobile}
      isAutoClose={false}
      isCloseIcon
      open={open}
      onClose={onClose}
    >
      <div className={styles.container}>
        <div
          className={!isMobile ? styles.subContainer : styles.mobileContainer}
        >
          {!choice && (
            <div className={styles.iconContainer}>
              <img src={HeaderIcon} alt="" />
            </div>
          )}
          <div className={styles.headerContainer}>
            {header1}
            <br />
            {header2}
          </div>
          {showEasyEditPlan && renderEasyEditPlan(choice)}
          {showPredictiveOrder && renderPredictiveOrder(choice)}
          {showReplenish && renderReplenish(choice)}
        </div>
        <div className={styles.buttonContainer}>
          {!choice && (
            <Button
              className={styles.cancelButton}
              text="Cancel"
              onClick={() => {
                onClose();

                dataLayerPush(
                  'SelfServe',
                  {
                    event: 'type-change-cancel',
                    section: 'edit recurring order',
                    id: open.itemNumber,
                    name: open.name,
                    brand: open.brand || '',
                    category: open.category,
                    variant: open.size,
                    quantity: open.quantity,
                    deliveryFrequency: switchTo,
                  },
                  'dlS13',
                );
              }}
              type="button"
              color="gray"
            />
          )}
          {!choice && (
            <Button
              className={styles.saveButton}
              onClick={() => {
                onSave(switchTo);

                dataLayerPush(
                  'SelfServe',
                  {
                    event: 'type-change-submit',
                    section: 'edit recurring order',
                    id: open.itemNumber,
                    name: open.name,
                    brand: open.brand || '',
                    category: open.category,
                    variant: open.size,
                    quantity: open.quantity,
                    deliveryFrequency: switchTo,
                  },
                  'dlS14',
                );
              }}
              text="Confirm"
            />
          )}
          {choice && (
            <Button
              className={styles.saveButton}
              onClick={(e) => {
                onClose(e);
              }}
              text="Got it, thanks!"
            />
          )}
        </div>
      </div>
    </Dialog>
  );
};

WhatAreMyOptionsDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  isResidential: PropTypes.bool,
  isReturnable: PropTypes.bool,
  switchTo: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func,

  choice: PropTypes.bool,
};

export default WhatAreMyOptionsDialog;
